/* eslint-disable no-restricted-syntax */
import { useReducer, useState, useCallback, useContext, useEffect } from 'react';
import { AxiosError } from 'axios';

import * as Sentry from '@sentry/react';

import dayjs from 'dayjs';
import advanced from 'dayjs/plugin/advancedFormat';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { notifications } from '@mantine/notifications';

import { DATE_MDY, LOCAL_LAST_SAVE_DATE } from 'constants/dateTime';
import {
  CUBOH_INTEGRATION,
  CUBOH_MENUS,
  CUBOH_MENU_CHILD_MAP,
  ITEM_TYPES_FOR_PARENT_CHILD,
  MENU_OBJECT_TYPES,
  SAVE_ERROR_MESSAGE,
  SINGLE_MENU,
} from 'constants/menuManagement';
import { NOTIFICATION_VARIANTS } from 'constants/chownow';
import { CUBOH_TAGS } from 'constants/cuboh';

import {
  setAllParentChildOptions,
  setParentChildError,
  setParentChildIsLoading,
  setMerchantIntegrations,
  setMerchantIntegrationsLoading,
  setMerchantIntegrationsError,
  setSupportedIntegrations,
  setSupportedIntegrationsLoading,
  setSupportedIntegrationsError,
  setAvailableClassifications,
  setClassificationsLoading,
  setClassificationsError,
  setCubohMenuItems,
  setIsLoading,
  setError,
  updateCubohMenuItem,
  setSingleMenu,
} from 'actions/menuManagement';

import { reducer, DEFAULT_STATE } from 'reducers/menuManagement';

import {
  fetchCubohMenus,
  fetchCubohItemChildren,
  normalizeCubohMenuItems,
  patchCubohMenus,
  patchMerchantMenuOrdinals,
  normalizeCubohMenus,
  createNewCubohItemHelper,
  fetchItemsByType,
  duplicateCubohMenuInMenuManagement,
  fetchSupportedIntegrations,
  fetchClassifications,
  getActiveAppIntegrationsByMerchant,
  fetchResourcePOS,
  getIntegrationsByStatus,
  getReadOnlySyncData,
} from 'helpers/menuManagement';

import { useAuth } from 'hooks/useAuth';
import { useCubohActionBar } from 'hooks/useCubohActionBar';
import ErrorBoundaryContext from 'context/ErrorBoundaryContext';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(advanced);

export default function useProvideMenuManagement() {
  const [state, dispatch] = useReducer(reducer, DEFAULT_STATE);
  const [autoSaveLabel, setAutoSaveLabel] = useState(null);
  const [showAllFields, setShowAllFields] = useState(null);
  const [menuEditID, setMenuEditID] = useState(null);
  const [createMenu, setCreateMenu] = useState(null);
  const [tempSortedMenus, setTempSortedMenus] = useState(null);
  const [sortedMenus, setSortedMenus] = useState(null);
  const [posResource, setPosResource] = useState(null);
  const [importsInProgress, setImportsInProgress] = useState({});
  const [readOnlySyncData, setReadOnlySyncData] = useState({});

  const {
    cubohUser,
    cubohUser: {
      userDetails: { userMeta },
      isParentCompanyUser,
    },
    companies,
    isInternalUser,
  } = useAuth();

  const { singleSelectedCompanyID, singleSelectedMerchantID, selectedTimezone } =
    useCubohActionBar();
  const { setError: setErrorBoundaryError } = useContext(ErrorBoundaryContext);

  const localTimeZone = dayjs().tz(selectedTimezone).format(`${DATE_MDY}z`);

  useEffect(() => {
    const fetchImportStatus = async () => {
      if (!(singleSelectedCompanyID && singleSelectedMerchantID)) return;
      try {
        const { data } = await getIntegrationsByStatus(
          singleSelectedCompanyID,
          singleSelectedMerchantID,
          CUBOH_TAGS.IMPORT_IN_PROGRESS,
        );
        setImportsInProgress(data.integrations);
      } catch (error) {
        setImportsInProgress({});
      }
    };
    fetchImportStatus();
  }, [singleSelectedCompanyID, singleSelectedMerchantID]);

  useEffect(() => {
    const fetchReadOnlySyncData = async () => {
      if (!(singleSelectedCompanyID && singleSelectedMerchantID)) return;
      try {
        const { data } = await getReadOnlySyncData(
          singleSelectedCompanyID,
          singleSelectedMerchantID,
        );
        setReadOnlySyncData(data);
      } catch (error) {
        setReadOnlySyncData({});
      }
    };
    fetchReadOnlySyncData();
  }, [singleSelectedCompanyID, singleSelectedMerchantID]);

  const posRefreshInProgress = Object.values(importsInProgress).includes('pos');

  const getCubohMenus = useCallback(async () => {
    if (!singleSelectedCompanyID) return;

    try {
      dispatch(
        setError({
          parentGroup: CUBOH_MENUS,
          group: MENU_OBJECT_TYPES.menus,
          hasError: false,
          error: null,
        }),
      );
      dispatch(
        setIsLoading({
          parentGroup: CUBOH_MENUS,
          group: MENU_OBJECT_TYPES.menus,
          isLoading: true,
        }),
      );

      const normalizedData = await fetchCubohMenus({
        companyID: singleSelectedCompanyID,
        merchantID: singleSelectedMerchantID || 0,
        isParentCompanyUser,
        setError: setErrorBoundaryError,
        cubohUser,
        companies,
        singleSelectedCompanyID,
        isInternalUser,
      });
      dispatch(
        setCubohMenuItems({
          normalizedData,
          itemType: MENU_OBJECT_TYPES.menus,
        }),
      );
    } catch (e) {
      Sentry.captureException(e);
      dispatch(
        setError({
          parentGroup: CUBOH_MENUS,
          group: MENU_OBJECT_TYPES.menus,
          hasError: true,
          message: e.message,
        }),
      );
    } finally {
      dispatch(
        setIsLoading({
          parentGroup: CUBOH_MENUS,
          group: MENU_OBJECT_TYPES.menus,
          isLoading: false,
        }),
      );
    }
  }, [singleSelectedCompanyID, singleSelectedMerchantID, setErrorBoundaryError]);

  const resetSingleMenuState = () => {
    dispatch(setSingleMenu(DEFAULT_STATE.singleMenu));
  };

  const getSingleCubohMenu = async (companyID, merchantID, menuID, searchUuid = '') => {
    try {
      dispatch(
        setIsLoading({
          parentGroup: SINGLE_MENU,
          group: MENU_OBJECT_TYPES.single,
          isLoading: true,
        }),
      );
      dispatch(
        setError({
          parentGroup: SINGLE_MENU,
          group: MENU_OBJECT_TYPES.single,
          hasError: false,
          error: null,
        }),
      );

      const normalizedData = await fetchCubohMenus({
        companyID,
        merchantID: merchantID || 0,
        menuID,
        isParentCompanyUser,
        cubohUser,
        companies,
        singleSelectedCompanyID,
        isInternalUser,
      });
      const rawChildrenResponse = await fetchCubohItemChildren({
        companyID,
        merchantID: merchantID || 0,
        parentUuid: menuID,
        searchUuid,
      });
      const childItemType = rawChildrenResponse.itemType;
      const normalizedChildren = normalizeCubohMenuItems(rawChildrenResponse.items);
      dispatch(
        setSingleMenu({
          normalizedData,
          itemType: MENU_OBJECT_TYPES.single,
        }),
      );

      dispatch(
        setCubohMenuItems({
          normalizedData: normalizedChildren,
          itemType: childItemType,
        }),
      );
    } catch (e) {
      Sentry.captureException(e);
      dispatch(
        setError({
          parentGroup: SINGLE_MENU,
          group: MENU_OBJECT_TYPES.single,
          hasError: true,
          message: e.message,
        }),
      );
    } finally {
      dispatch(
        setIsLoading({
          parentGroup: SINGLE_MENU,
          group: MENU_OBJECT_TYPES.single,
          isLoading: false,
        }),
      );
    }
  };

  // isNestedSomewhere is BE-truth across all placements - broadcast it to a mod group's other
  // cached placements whenever a children-changing save's response reveals it. Only called from
  // updateChildren, not updateSimilarItems, which fires on every save regardless of relevance.
  const propagateModGroupNestedStatusFromChildren = children => {
    const modGroupData = state?.cubohMenus[MENU_OBJECT_TYPES.modifierGroups]?.data ?? {};
    const updatedInstances = [];

    (children ?? [])
      .filter(child => child?.itemType === MENU_OBJECT_TYPES.modifierGroups)
      .forEach(child => {
        let isNestedSomewhere;
        if (child.isNested || child.isNestedSomewhere === true) {
          // trust isNestedSomewhere: true from a thin PATCH summary too, even without isNested -
          // a stale true is over-restrictive, not unsafe, so it's fine as a backup signal
          isNestedSomewhere = true;
        } else if (child.isNestedSomewhere === false) {
          isNestedSomewhere = false;
        } else {
          return;
        }

        const instances = Object.values(modGroupData).filter(item => item?.uuid === child?.uuid);
        updatedInstances.push(...instances.map(item => ({ ...item, isNestedSomewhere })));
      });

    if (updatedInstances.length) {
      dispatch(
        setCubohMenuItems({
          itemType: MENU_OBJECT_TYPES.modifierGroups,
          normalizedData: normalizeCubohMenuItems(updatedInstances),
        }),
      );
    }
  };

  const updateSimilarItems = async ({ response }) => {
    if (response.itemType === MENU_OBJECT_TYPES.menus) return;
    // find similar items in state
    const similarItems = Object.values(state?.cubohMenus[response.itemType]?.data ?? {}).filter(
      item => item?.uuid === response?.uuid && item?.parentUuid !== response?.parentUuid,
    );
    // TODO: CHECK TO MAKE SURE THIS DIDN"T BREAK SINCE I MADE A SWITCH"
    const updatedItems = similarItems.map(item => ({
      ...item,
      ...response,
      numberOfChildren: response?.children?.length,
      key: item.key,
      ordinal: item.ordinal,
      parentUuid: item.parentUuid,
      enabled: item.enabled,
    }));

    dispatch(
      setCubohMenuItems({
        itemType: response.itemType,
        normalizedData: normalizeCubohMenuItems(updatedItems),
      }),
    );
  };

  const updateParents = async ({ response, data }) => {
    // delete and/or adapt the existing item for each parent
    const responseKey = `${response?.uuid}_${response?.parentUuid}`;
    const updatedItems = [];
    if (!data?.parents?.length) return;
    for (const parent of data.parents) {
      const key = `${response?.uuid}_${parent?.uuid}`;
      if (parent.isDeleted) {
        dispatch(
          updateCubohMenuItem({
            group: response.itemType,
            id: key,
            data: false,
          }),
        );
        // eslint-disable-next-line no-continue
        continue;
      }

      // eslint-disable-next-line max-len
      // ensure that all instances of the parent have this in their children array and update in state
      const parentsInState = Object.values(state?.cubohMenus[parent.itemType]?.data ?? {}).filter(
        item => item?.uuid === parent?.uuid,
      );

      for (const stateParent of parentsInState) {
        stateParent.children = stateParent.children.filter(item => item?.uuid !== response?.uuid);
        if (!response?.isDeleted) {
          stateParent.children.push({
            isDeleted: false,
            uuid: response?.uuid,
            name: response?.name,
            itemType: response?.itemType,
          });
        }
        stateParent.numberOfChildren = stateParent.children.length;
      }

      if (parentsInState.length) {
        dispatch(
          setCubohMenuItems({
            itemType: parentsInState[0].itemType,
            normalizedData: normalizeCubohMenuItems(parentsInState),
          }),
        );
      }

      const itemEnabled =
        key === responseKey
          ? response.enabled
          : (state?.cubohMenus[response.itemType]?.data[key]?.enabled ?? true);

      if (!response.isDeleted) {
        updatedItems.push({
          ...data,
          ...response,
          key,
          isDeleted: false,
          numberOfChildren: response.children.length,
          parentUuid: parent?.uuid,
          enabled: itemEnabled,
        });
      }
    }

    if (updatedItems.length) {
      dispatch(
        setCubohMenuItems({
          normalizedData: normalizeCubohMenuItems(updatedItems),
          itemType: updatedItems[0].itemType,
        }),
      );
    }
  };

  const updateChildren = async ({ response, data }) => {
    if (!data?.children?.length) return;
    for (const child of data.children) {
      const childrenInState = Object.values(state?.cubohMenus[child.itemType]?.data ?? {}).filter(
        item => item?.uuid === child?.uuid,
      );

      // no details for the child are stored in state yet, so need to fetch
      if (!childrenInState.length) {
        let rawChildrenResponse;
        try {
          // eslint-disable-next-line no-await-in-loop
          rawChildrenResponse = await fetchCubohItemChildren({
            companyID: singleSelectedCompanyID,
            merchantID: response?.merchantId,
            parentUuid: response?.uuid,
          });
        } catch (e) {
          Sentry.captureException(e);
          return;
        }
        const childTypeData = state?.cubohMenus[child.itemType]?.data;
        const deletedCheckedState = [
          ...Object.keys(childTypeData).map(childItemKey => {
            const parentMatchesData = childTypeData[childItemKey].parentUuid === data?.uuid;
            const childInResponse = response?.children.filter(
              filterChild => filterChild?.uuid === childTypeData[childItemKey]?.uuid,
            ).length;
            if (parentMatchesData && !childInResponse) {
              return {
                ...childTypeData[childItemKey],
                isDeleted: true,
              };
            }
            return childTypeData[childItemKey];
          }),
          ...rawChildrenResponse.items,
        ];
        if (rawChildrenResponse.items.length) {
          dispatch(
            setCubohMenuItems({
              normalizedData: normalizeCubohMenuItems(deletedCheckedState),
              itemType: child.itemType,
            }),
          );
        }
        propagateModGroupNestedStatusFromChildren(rawChildrenResponse.items);
        // if this case happens, we don't need to evaluate further
        // it does all children
        return;
      }
    }

    // if we don't return above, we have all the data we need in state
    // we can just delete and/or adapt an existing item

    // response.children is a thin PATCH summary and doesn't carry isNested - only the dedicated
    // /children GET endpoint returns full records with it, so fetch that when this modifier has
    // a non-deleted mod-group child (not the ordinary item -> mod group relationship, which would
    // make this fetch fire on every item save). This also re-fetches on steady-state saves of an
    // already-nested modifier, not only the first nesting - accepted for now, see follow-up to
    // skip the fetch when the child is already known in state.
    const hasNonDeletedModGroupChildUnderModifier =
      response?.itemType === MENU_OBJECT_TYPES.modifiers &&
      data.children.some(
        child => !child.isDeleted && child.itemType === MENU_OBJECT_TYPES.modifierGroups,
      );
    let freshModGroupChildren = null;
    if (hasNonDeletedModGroupChildUnderModifier) {
      try {
        freshModGroupChildren =
          (
            await fetchCubohItemChildren({
              companyID: singleSelectedCompanyID,
              merchantID: response?.merchantId,
              parentUuid: response?.uuid,
            })
          )?.items ?? null;
      } catch (e) {
        // the save already succeeded; a failed cache-consistency fetch must not surface as a save error
        Sentry.captureException(e);
      }
    }

    const updatedChildren = [];
    for (const child of data.children) {
      const key = `${child?.uuid}_${response?.uuid}`;
      const childFromResponse =
        freshModGroupChildren?.find(fc => fc?.uuid === child?.uuid) ??
        response?.children?.find(rc => rc?.uuid === child?.uuid);

      const childInState = Object.values(state?.cubohMenus[child.itemType]?.data ?? {}).filter(
        item => item?.uuid === child?.uuid,
      )[0];

      if (child.isDeleted) {
        dispatch(updateCubohMenuItem({ group: child.itemType, id: key, data: false }));
      } else {
        // otherwise we take the existing item details in state and create a copy
        // under a different parent (new key and new parent uuid)
        updatedChildren.push({
          ...childInState,
          ...childFromResponse,
          key,
          isDeleted: false,
          parentUuid: response?.uuid,
        });
      }
    }

    if (updatedChildren.length) {
      dispatch(
        setCubohMenuItems({
          normalizedData: normalizeCubohMenuItems(updatedChildren),
          itemType: updatedChildren[0].itemType,
        }),
      );
    }

    propagateModGroupNestedStatusFromChildren(freshModGroupChildren ?? response?.children);
  };

  const updateLocalState = async ({ group, id, data }) => {
    dispatch(updateCubohMenuItem({ group, id, data }));
  };

  const findMainItemParentsAndChildren = (key, itemType) => ({
    children: state.cubohMenus[itemType]?.data[key]?.children,
    parents: state.cubohMenus[itemType]?.data[key]?.parents,
  });

  const updateParentMenuValidation = item => {
    // find parent menu or if it is a menu
    // update validation state and dispatch the change
    if (!item || item.itemType === MENU_OBJECT_TYPES.menus || !item.parentMenus) return;
    for (const parentMenuId of item.parentMenus) {
      const menusInState =
        state?.cubohMenus?.[MENU_OBJECT_TYPES.menus]?.data[Number(item.merchantId)];
      const parentMenuInState = menusInState?.find(menu => menu.uuid === parentMenuId);
      // eslint-disable-next-line no-continue
      if (!parentMenuInState) continue;
      updateLocalState({
        group: MENU_OBJECT_TYPES.menus,
        id: parentMenuId,
        data: { ...parentMenuInState, menuIsValidated: false },
      });
    }
  };

  /* `omitRelationships` is for callers with no user intent about parents/children - the
  POS-locked edit drawer (CN-50652), whose pickers are read-only. Sending an empty list there
  would read as "detach from everything", and the backfill below can't help: state entries are
  seeded with `parents: []` by normalizeCubohMenuItems, so it merges the same empty list back
  in. Leaving the keys out of the PATCH entirely is the only way to say "don't touch these". */
  // eslint-disable-next-line consistent-return
  const updateCubohMenu = async ({ group, id, data, omitRelationships = false }) => {
    if (omitRelationships) {
      const { parents: _parents, children: _children, ...withoutRelationships } = data;
      // eslint-disable-next-line no-param-reassign
      data = withoutRelationships;
    } else if (!data.parents || !data.children) {
      // eslint-disable-next-line no-param-reassign
      data = {
        ...findMainItemParentsAndChildren(id, group),
        ...data,
      };
    }
    try {
      dispatch(
        setError({
          parentGroup: CUBOH_MENUS,
          group,
          hasError: false,
          error: null,
        }),
      );

      const response = await patchCubohMenus({
        companyID: data.companyId || singleSelectedCompanyID,
        merchantID: data.merchantId || singleSelectedMerchantID || 0,
        itemID: data?.uuid,
        items: [data],
      });

      // update data to ensure it matches the response from the backend
      const dataForState = response?.isDeleted
        ? { merchantId: data?.merchantId, isDeleted: true }
        : {
            ...data,
            ...response,
            savedChildren: false,
            menuIsValidated: false,
          };
      updateParentMenuValidation(dataForState);
      dispatch(updateCubohMenuItem({ group, id, data: dataForState }));

      if (!response?.isDeleted) {
        // update duplicates (of different parents) of the edited item
        await updateSimilarItems({ response });

        // update all instances of this item's parents
        await updateParents({ response, data });

        // update all instances of this item's parent's children
        await updateChildren({ response, data });
      }

      setAutoSaveLabel({
        hasError: false,
        group,
        data,
        lastSuccessDate: `${dayjs().local().format(LOCAL_LAST_SAVE_DATE)} `,
      });

      return response;
    } catch (e) {
      setAutoSaveLabel({
        ...autoSaveLabel,
        hasError: true,
        group,
        data,
      });

      const message = e.response?.data?.errorMessages?.[0] || SAVE_ERROR_MESSAGE;

      throw new Error(message);
    } finally {
      dispatch(
        setIsLoading({
          parentGroup: CUBOH_MENUS,
          group,
          isLoading: false,
        }),
      );
    }
  };

  // eslint-disable-next-line no-shadow
  const updateMerchantMenuOrdinals = async ({ group, data, merchantID, setTempSortedMenus }) => {
    const itemOrdinals = data[merchantID].map(menu => {
      const { uuid, parentUuid, ordinal } = menu;
      return { uuid, parent_uuid: parentUuid, ordinal };
    });

    try {
      const response = await patchMerchantMenuOrdinals({
        companyID: singleSelectedCompanyID,
        merchantID,
        items: itemOrdinals,
      });

      // replace the ordinals of the menus in data with the once returned from the backend
      const dataUpdatedOrdinals = data[merchantID].map(existingItem => {
        const { ordinal } = response.find(item => item?.uuid === existingItem?.uuid);
        return { ...existingItem, ordinal };
      });

      dispatch(
        setCubohMenuItems({
          normalizedData: normalizeCubohMenus(
            dataUpdatedOrdinals,
            isParentCompanyUser,
            cubohUser,
            companies,
            singleSelectedCompanyID,
            isInternalUser,
          ),
          group,
          itemType: 'menu_ordinal',
        }),
      );

      setAutoSaveLabel({
        hasError: false,
        group,
        data,
        lastSuccessDate: `${dayjs().local().format(LOCAL_LAST_SAVE_DATE)} `,
      });
    } catch (e) {
      Sentry.captureException(e);
      setTempSortedMenus(null);
      setAutoSaveLabel({
        ...autoSaveLabel,
        hasError: true,
        group,
        data,
      });
    }
  };

  const fetchAllOptionsForParentChildSelectors = async menu => {
    dispatch(
      setParentChildError({
        menuId: menu?.uuid,
        hasError: false,
        error: null,
      }),
    );
    dispatch(
      setParentChildIsLoading({
        menuId: menu?.uuid,
        isLoading: true,
      }),
    );

    // for each item type, fetch all and store in state
    // this is to ensure that the parent child selector has all
    // the data, without having to fetch on every drawer open action
    const allParentChildOptions = {};
    for (const itemType of ITEM_TYPES_FOR_PARENT_CHILD) {
      try {
        // could probably be made quicker by doing requests in parallel
        // and using promise.all
        // eslint-disable-next-line no-await-in-loop
        const response = await fetchItemsByType({
          companyId: menu?.companyId,
          merchantId: menu?.merchantId,
          menuId: menu?.uuid,
          itemType,
        });
        allParentChildOptions[itemType] = response;
      } catch (e) {
        allParentChildOptions[itemType] = [];
        // a 404 is somewhat normal (menus may not have items of all types)
        // we can just set it to blank and not show an error
        if (e.response?.status !== 404) {
          // if it is a non-404 error, set an error state to show the user
          dispatch(
            setParentChildError({
              menuId: menu?.uuid,
              hasError: true,
              error: e.response?.data?.message ?? e.message,
            }),
          );
        }
      }
    }
    dispatch(
      setAllParentChildOptions({
        allParentChildOptions,
        menuId: menu?.uuid,
      }),
    );
    dispatch(
      setParentChildIsLoading({
        menuId: menu?.uuid,
        isLoading: false,
      }),
    );
  };

  const updateParentChildOptions = async ({ parentMenus, merchantId, companyId }) => {
    // find the parent menus of the new item which matches state
    // this will practically always only be ONE, as there should only be one menu in state
    const menusToUpdate = parentMenus?.filter(menuUuid =>
      Object.keys(state.allParentChildOptions).includes(menuUuid),
    );
    // then refresh the parent/child options
    // we loop it just in case it is more than one
    for (const menu of menusToUpdate) {
      fetchAllOptionsForParentChildSelectors({
        uuid: menu,
        companyId,
        merchantId,
      });
    }
  };

  // eslint-disable-next-line consistent-return
  const createNewCubohItem = async ({ group, data }) => {
    const creatorMerchantId = data?.merchantId || singleSelectedMerchantID;

    try {
      dispatch(
        setError({
          parentGroup: CUBOH_MENUS,
          group,
          hasError: false,
          error: null,
        }),
      );

      const response = await createNewCubohItemHelper({
        companyID: singleSelectedCompanyID,
        merchantID: creatorMerchantId,
        items: [data],
      });
      const newItem = normalizeCubohMenus(
        [response],
        isParentCompanyUser,
        cubohUser,
        companies,
        singleSelectedCompanyID,
        isInternalUser,
      );
      dispatch(setCubohMenuItems({ group, itemType: group, normalizedData: newItem }));

      await updateParents({ response, data: response });
      setAutoSaveLabel({
        hasError: false,
        group,
        data,
        lastSuccessDate: `${dayjs().format(LOCAL_LAST_SAVE_DATE)} ${localTimeZone}`,
      });
      const newItemForParentChild = newItem[creatorMerchantId]?.[0];
      if (!newItemForParentChild?.parentMenus) {
        newItemForParentChild.parentMenus = [];
      }
      updateParentChildOptions(newItemForParentChild);

      return response;
    } catch (e) {
      if (!(e instanceof AxiosError)) {
        e.name = '[MENU MANAGEMENT] Failed to create new menu item';
        Sentry.captureException(e, {
          extra: {
            group,
            merchant_id: creatorMerchantId,
            company_id: singleSelectedCompanyID,
            error_message: e.message,
          },
        });
      }
      setAutoSaveLabel({
        ...autoSaveLabel,
        hasError: true,
        group,
        data,
      });
      throw e;
    } finally {
      dispatch(
        setIsLoading({
          parentGroup: CUBOH_MENUS,
          group,
          isLoading: false,
        }),
      );
    }
  };

  const findCubohMenuItemChildren = ({ parentUuid, childItemType }) => {
    const menuItems = state.cubohMenus[childItemType]?.data;

    const menuItemData = Object.values(menuItems);
    return menuItemData
      .filter(menuItem => menuItem.parentUuid === parentUuid)
      .sort((a, b) => a.ordinal - b.ordinal);
  };

  const getParentChildDataByType = ({ menuId, itemType }) =>
    state.allParentChildOptions?.[menuId]?.[itemType] || [];

  const getParentChildLoadingState = ({ menuId }) => !!state.parentChildIsLoading?.[menuId];

  const getParentChildErrorState = ({ menuId }) => !!state.parentChildError?.[menuId]?.hasError;

  const getCubohMenuItemChildren = async (record, searchUuid = '') => {
    if (!record) return;
    if (!record.savedChildren) {
      try {
        dispatch(
          setError({
            parentGroup: CUBOH_MENUS,
            group: CUBOH_MENU_CHILD_MAP[record.itemType],
            hasError: false,
            error: null,
          }),
        );
        dispatch(
          setIsLoading({
            parentGroup: CUBOH_MENUS,
            group: CUBOH_MENU_CHILD_MAP[record.itemType],
            isLoading: true,
          }),
        );

        const response = await fetchCubohItemChildren({
          companyID: singleSelectedCompanyID,
          merchantID: record?.merchantId,
          parentUuid: record?.uuid,
          searchUuid,
        });
        const normalizedData = normalizeCubohMenuItems(response.items);
        dispatch(
          setCubohMenuItems({
            normalizedData,
            itemType: response.itemType,
          }),
        );

        const { children, parents } = findMainItemParentsAndChildren(record.key, record.itemType);
        dispatch(
          updateCubohMenuItem({
            group: record.itemType,
            id: record.key,
            data: { ...record, parents, children, savedChildren: true },
          }),
        );
      } catch (e) {
        Sentry.captureException(e);
        dispatch(
          setError({
            parentGroup: CUBOH_MENUS,
            group: CUBOH_MENU_CHILD_MAP[record.itemType],
            hasError: true,
            message: e.message,
          }),
        );
      } finally {
        dispatch(
          setIsLoading({
            parentGroup: CUBOH_MENUS,
            group: CUBOH_MENU_CHILD_MAP[record.itemType],
            isLoading: false,
          }),
        );
      }
    }
  };

  const duplicateCubohMenu = async ({
    companyId,
    merchantId,
    menuId,
    targetMerchants,
    newMenuName,
  }) => {
    try {
      await duplicateCubohMenuInMenuManagement({
        companyId,
        merchantId,
        menuId,
        targetMerchants,
        newMenuName,
      });
    } catch (e) {
      if (!(e instanceof AxiosError)) {
        e.name = '[MENU MANAGEMENT] Failed to duplicate menu';
        Sentry.captureException(e, {
          extra: {
            menu_id: menuId,
            merchant_id: merchantId,
            company_id: companyId,
            error_message: e.message,
          },
        });
      }
      notifications.show({
        message: (
          <span data-testid="menu-copy-failed-notification">
            Something went wrong while copying your menu. Please try again or contact support for
            assistance.
          </span>
        ),
        closeButtonProps: { 'data-testid': 'notification-close-button' },
        variant: NOTIFICATION_VARIANTS.caution.label,
        icon: NOTIFICATION_VARIANTS.caution.icon,
        withCloseButton: true,
        autoClose: false,
      });

      throw e;
    }
  };

  const triggerItemReRenderWithLocalData = ({ itemType, itemUuid, updatedData }) => {
    try {
      dispatch(
        updateCubohMenuItem({
          group: itemType,
          id: itemUuid,
          data: updatedData,
        }),
      );
    } catch (e) {
      Sentry.captureException(e);
      dispatch(
        setError({
          parentGroup: CUBOH_MENUS,
          group: itemType,
          hasError: true,
          message: e.message,
        }),
      );
    } finally {
      dispatch(
        setIsLoading({
          parentGroup: CUBOH_MENUS,
          group: itemType,
          isLoading: false,
        }),
      );
    }
  };

  const removeFilteredFromState = async itemType => {
    if (itemType !== MENU_OBJECT_TYPES.menus) {
      // eslint-disable-next-line guard-for-in
      for (const currentChildKey in state.cubohMenus[itemType].data) {
        dispatch(
          updateCubohMenuItem({
            group: itemType,
            id: currentChildKey,
            data: false,
          }),
        );
      }
    }
    if (itemType !== MENU_OBJECT_TYPES.modifiers) {
      return removeFilteredFromState(CUBOH_MENU_CHILD_MAP[itemType]);
    }

    return true;
  };

  const refreshSingleMenuUpdateSearchTerm = async ({
    companyId,
    merchantId,
    menuUuid,
    searchUuid,
  }) => {
    await removeFilteredFromState(MENU_OBJECT_TYPES.menus);
    await getSingleCubohMenu(companyId, merchantId, menuUuid, searchUuid);
  };

  const getSupportedIntegrationsFromState = () => state.supportedIntegrations || [];

  const getSupportedIntegrationsLoadingState = () => !!state.supportedIntegrationsIsLoading;

  const getSupportedIntegrationsErrorState = () => !!state.supportedIntegrationsError?.hasError;

  const fetchAllSupportedIntegrations = async companyId => {
    dispatch(
      setSupportedIntegrationsError({
        hasError: false,
        error: null,
      }),
    );
    dispatch(
      setSupportedIntegrationsLoading({
        isLoading: true,
      }),
    );

    let supportedIntegrations = [];
    try {
      const response = await fetchSupportedIntegrations({
        companyID: companyId,
      });
      supportedIntegrations = response ?? [];
    } catch (e) {
      supportedIntegrations = [];
      dispatch(
        setParentChildError({
          hasError: true,
          error: e?.response?.data?.message ?? e?.message,
        }),
      );
    }
    dispatch(setSupportedIntegrations({ supportedIntegrations }));
    dispatch(
      setSupportedIntegrationsLoading({
        isLoading: false,
      }),
    );
  };

  const getAvailableClassificationsFromState = () => state.availableClassifications || null;

  const getClassificationsLoadingState = () => !!state.classificationsIsLoading;

  const getClassificationsErrorState = () => !!state.classificationsError?.hasError;

  const fetchAvailableClassifications = async companyId => {
    if (!companyId) {
      dispatch(setAvailableClassifications({ availableClassifications: null }));
      dispatch(setClassificationsError({ hasError: false, error: null }));
      dispatch(setClassificationsLoading({ isLoading: false }));
      return;
    }

    dispatch(
      setClassificationsError({
        hasError: false,
        error: null,
      }),
    );
    dispatch(
      setClassificationsLoading({
        isLoading: true,
      }),
    );

    let availableClassifications = null;
    try {
      const response = await fetchClassifications({
        companyID: companyId,
      });
      availableClassifications = response;
    } catch (e) {
      Sentry.captureException(e, {
        extra: {
          company_id: companyId,
        },
      });
      availableClassifications = null;
      dispatch(
        setClassificationsError({
          hasError: true,
          error: e?.response?.data?.message ?? e?.message,
        }),
      );
    }
    dispatch(setAvailableClassifications({ availableClassifications }));
    dispatch(
      setClassificationsLoading({
        isLoading: false,
      }),
    );
  };

  const getMerchantIntegrationsFromState = (merchantId, addCubohAsIntegration) => {
    // eslint-disable-next-line no-param-reassign
    merchantId = merchantId.toString();
    try {
      if (!addCubohAsIntegration) {
        return Object.fromEntries(
          Object.entries(state.merchantIntegrations[merchantId]).filter(
            ([integrationName]) => integrationName !== CUBOH_INTEGRATION,
          ),
        );
      }
      return state.merchantIntegrations[merchantId];
    } catch (e) {
      return null;
    }
  };

  const getMerchantIntegrationsLoadingState = merchantId =>
    !!state.merchantIntegrationsIsLoading?.[merchantId];

  const getMerchantIntegrationsErrorState = merchantId =>
    !!state.merchantIntegrationsError?.[merchantId]?.hasError;

  const fetchMerchantIntegrations = async (companyId, merchantId) => {
    if (typeof merchantId === 'undefined') {
      Sentry.captureException(
        new Error('cannot fetch merchant integrations when `merchantId` is undefined', {
          companyId,
        }),
      );
      return;
    }

    // eslint-disable-next-line no-param-reassign
    merchantId = merchantId?.toString();
    dispatch(
      setMerchantIntegrationsError({
        merchantId,
        hasError: false,
        error: null,
      }),
    );
    dispatch(
      setMerchantIntegrationsLoading({
        merchantId,
        isLoading: true,
      }),
    );

    let merchantIntegrations = {};
    try {
      // we always set addCubohAsIntegration to true
      // it gets filtered when we fetch from state in the components where
      // the legacy code used to make the request
      const integrationsResponse = await getActiveAppIntegrationsByMerchant(merchantId, true);
      const posResponse = await fetchResourcePOS(companyId);

      if (posResponse) {
        merchantIntegrations = {
          ...integrationsResponse,
          [posResponse.type]: {
            activeData: { id: null },
          },
          enforcePosConditions:
            posResponse.use_mm_endpoint || userMeta?.isMigratingShooter || false,
          // the user meta flag is secondary to the use_mm_mapping flag moving forward
          useV2Mapping: posResponse.use_mm_mapping || false,
          isPosSyncInPlace: posResponse.mm_sync_in_place || false,
          isPosAutoPublishOnSync: posResponse.mm_auto_publish || false,
        };
      } else if (userMeta?.isMigratingShooter) {
        merchantIntegrations = {
          ...integrationsResponse,
          enforcePosConditions: true,
        };
      } else merchantIntegrations = integrationsResponse;
    } catch (e) {
      Sentry.captureException(e);
      dispatch(
        setMerchantIntegrationsError({
          merchantId,
          hasError: true,
          error: e?.response?.data?.message ?? e?.message,
        }),
      );
    }
    dispatch(setMerchantIntegrations({ merchantId, merchantIntegrations }));
    dispatch(
      setMerchantIntegrationsLoading({
        merchantId,
        isLoading: false,
      }),
    );
  };

  const checkParentMenuHasIntegration = (integrationNameArray, record) => {
    if (showAllFields) return true;

    if (!integrationNameArray) return false;
    // for menus
    if (record?.itemType === MENU_OBJECT_TYPES.menus) {
      return record?.integrations?.filter(integration =>
        integrationNameArray.includes(integration.integrationName),
      ).length;
    }
    // for other data types sent as singleMenu
    const integrations = record?.data?.[Object.keys(record.data)?.[0]]?.[0]?.integrations;

    if (!integrations) return false;

    return (
      integrations.filter(integration => integrationNameArray.includes(integration.integrationName))
        .length !== 0
    );
  };

  const menuToEdit = sortedMenus?.[menuEditID?.merchantID]?.find(
    menu => menu?.uuid === menuEditID?.menuUuid,
  );

  useEffect(() => {
    if (!singleSelectedCompanyID) return;
    (async () => {
      const resource = await fetchResourcePOS(singleSelectedCompanyID);
      setPosResource(resource);
    })();
  }, [singleSelectedCompanyID]);

  return {
    getCubohMenus,
    getSingleCubohMenu,
    resetSingleMenuState,
    companyID: singleSelectedCompanyID,
    resource: state.resource,
    posMenus: state.posMenus.menus,
    singleMenu: state.singleMenu[MENU_OBJECT_TYPES.single],
    cubohMenus: state.cubohMenus[MENU_OBJECT_TYPES.menus],
    cubohCategories: state.cubohMenus[MENU_OBJECT_TYPES.categories],
    cubohItems: state.cubohMenus[MENU_OBJECT_TYPES.items],
    cubohModifierGroups: state.cubohMenus[MENU_OBJECT_TYPES.modifierGroups],
    cubohModifiers: state.cubohMenus[MENU_OBJECT_TYPES.modifiers],
    allCubohItemsState: state.cubohMenus,
    updateCubohMenu,
    updateMerchantMenuOrdinals,
    updateLocalState,
    createNewCubohItem,
    findCubohMenuItemChildren,
    getCubohMenuItemChildren,
    autoSaveLabel,
    setAutoSaveLabel,
    duplicateCubohMenu,
    findMainItemParentsAndChildren,
    dispatch,
    triggerItemReRenderWithLocalData,
    refreshSingleMenuUpdateSearchTerm,
    fetchAllOptionsForParentChildSelectors,
    getParentChildDataByType,
    getParentChildLoadingState,
    getParentChildErrorState,
    updateParentChildOptions,
    fetchAllSupportedIntegrations,
    fetchMerchantIntegrations,
    getSupportedIntegrationsFromState,
    getSupportedIntegrationsLoadingState,
    getSupportedIntegrationsErrorState,
    fetchAvailableClassifications,
    getAvailableClassificationsFromState,
    getClassificationsLoadingState,
    getClassificationsErrorState,
    getMerchantIntegrationsFromState,
    getMerchantIntegrationsLoadingState,
    getMerchantIntegrationsErrorState,
    checkParentMenuHasIntegration,
    setShowAllFields,
    showAllFields,
    menuEditID,
    setMenuEditID,
    createMenu,
    setCreateMenu,
    tempSortedMenus,
    setTempSortedMenus,
    sortedMenus,
    setSortedMenus,
    menuToEdit,
    posResource,
    importsInProgress,
    posRefreshInProgress,
    readOnlySyncData,
  };
}
